import { useQuery, useMutation, useQueryClient } from "react-query";
import { apiGet, apiPost, apiPut } from "./api";
import { OrganizationAiCreditPurchase } from "../types/organizationAiCreditPurchase";

const getOrganizationAiCreditPurchase = async (): Promise<OrganizationAiCreditPurchase | null> => {
  return await apiGet({ path: "/ai_credit_purchases" });
};

function useOrganizationAiCreditPurchase() {
  return useQuery<OrganizationAiCreditPurchase | null>(["organizationAiCreditPurchase"], getOrganizationAiCreditPurchase, {
    refetchOnWindowFocus: false,
  });
}

interface CheckoutParams {
  stripePriceLookupKey: string;
}

const checkoutAiCreditTopUp = async (params: CheckoutParams) => {
  return await apiPost({
    path: "/ai_credit_purchases/checkout",
    variables: { organizationAiCreditPurchase: params },
  });
};

function useCheckoutAiCreditTopUp() {
  const queryClient = useQueryClient();
  return useMutation(checkoutAiCreditTopUp, {
    onSuccess: () => {
      queryClient.invalidateQueries(["organizationAiCreditPurchase"]);
    },
  });
}

interface PreviewSubscriptionChangeParams {
  priceId: string;
}

interface PreviewSubscriptionChangeResponse {
  amountDue: number;
  currency: string;
  lines: Array<{
    amount: number;
    description: string;
    price: { lookupKey: string };
  }>;
  subscriptionItemId: string;
  currentPeriodEnd: number;
  defaultPaymentMethod: {
    brand: string;
    last4: string;
  } | null;
}

interface CommitSubscriptionChangeParams {
  priceId: string;
}

const previewAiCreditSubscriptionChange = async (
  params: PreviewSubscriptionChangeParams,
): Promise<PreviewSubscriptionChangeResponse> => {
  return await apiPost({
    path: "/ai_credit_purchases/preview_subscription_change",
    variables: params,
  });
};

function usePreviewAiCreditSubscriptionChange() {
  return useMutation(previewAiCreditSubscriptionChange);
}

const commitAiCreditSubscriptionChange = async (
  params: CommitSubscriptionChangeParams,
) => {
  return await apiPost({
    path: "/ai_credit_purchases/update_ai_credit_subscription",
    variables: params,
  });
};

function useCommitAiCreditSubscriptionChange() {
  const queryClient = useQueryClient();
  return useMutation(commitAiCreditSubscriptionChange, {
    onSuccess: () => {
      queryClient.invalidateQueries(["organizationAiCreditPurchase"]);
      queryClient.invalidateQueries(["organizationAiCreditBalance"]);
      queryClient.invalidateQueries(["aiCreditCustomerSubscription"]);
    },
  });
}

interface PreviewTopUpParams {
  stripePriceLookupKey: string;
}

interface PreviewTopUpResponse {
  amountDue: number;
  currency: string;
  credits: number;
  defaultPaymentMethod: {
    brand: string;
    last4: string;
  } | null;
}

const previewAiCreditTopUp = async (
  params: PreviewTopUpParams,
): Promise<PreviewTopUpResponse> => {
  return await apiPost({
    path: "/ai_credit_purchases/preview_top_up",
    variables: params,
  });
};

function usePreviewAiCreditTopUp() {
  return useMutation(previewAiCreditTopUp);
}

interface TopUpParams {
  stripePriceLookupKey: string;
}

const purchaseAiCreditTopUp = async (params: TopUpParams) => {
  return await apiPost({
    path: "/ai_credit_purchases/purchase_top_up",
    variables: { organizationAiCreditPurchase: params },
  });
};

function usePurchaseAiCreditTopUp() {
  const queryClient = useQueryClient();
  return useMutation(purchaseAiCreditTopUp, {
    onSuccess: () => {
      queryClient.invalidateQueries(["organizationAiCreditBalance"]);
    },
  });
}

const purchaseAiCreditTopUpCheckoutSession = async (params: TopUpParams) => {
  // Flat (unwrapped) variables — mirrors the WWR checkout-session frontend, which posts
  // bare params with no wrapping key, consumed by the controller's `checkout_purchase_params`
  // (a bare `params.permit`, not `params.require(:organization_ai_credit_purchase)`).
  return await apiPost({
    path: "/ai_credit_purchases/purchase_top_up_checkout_session",
    variables: params,
  });
};

function usePurchaseAiCreditTopUpCheckoutSession() {
  return useMutation(purchaseAiCreditTopUpCheckoutSession);
}

const cancelAiCreditSubscription = async () => {
  return await apiPut({ path: "/ai_credit_purchases/cancel", variables: {} });
};

function useCancelAiCreditSubscription() {
  const queryClient = useQueryClient();
  return useMutation(cancelAiCreditSubscription, {
    onSuccess: () => {
      queryClient.invalidateQueries(["organizationAiCreditPurchase"]);
      queryClient.invalidateQueries(["organizationAiCreditBalance"]);
      queryClient.invalidateQueries(["aiCreditCustomerSubscription"]);
    },
  });
}

const revertAiCreditSubscriptionCancellation = async () => {
  return await apiPut({ path: "/ai_credit_purchases/revert_cancellation", variables: {} });
};

function useRevertAiCreditSubscriptionCancellation() {
  const queryClient = useQueryClient();
  return useMutation(revertAiCreditSubscriptionCancellation, {
    onSuccess: () => {
      queryClient.invalidateQueries(["organizationAiCreditPurchase"]);
      queryClient.invalidateQueries(["organizationAiCreditBalance"]);
      queryClient.invalidateQueries(["aiCreditCustomerSubscription"]);
    },
  });
}

const getOrganizationAiCreditPurchasePrices = async () => {
  return await apiGet({ path: "/ai_credit_purchases/prices" });
};

function useOrganizationAiCreditPurchasePrices({
  refetchOnWindowFocus = false,
}: {
  refetchOnWindowFocus?: boolean | "always";
}): {
  status: any;
  data: any;
  error: any;
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  refetch: () => any;
} {
  // window.logger("%c[useOrganizationAiCreditPurchase] useOrganizationAiCreditPurchasePrices", "color: #1976D2", { refetchOnWindowFocus });
  return useQuery(["organizationAiCreditPurchasePrices"], getOrganizationAiCreditPurchasePrices, {
    refetchOnWindowFocus,
  });
}

const getAiCreditCustomerSubscription = async () => {
  return await apiGet({ path: `/ai_credit_purchases/customer_subscription` });
};

function useAiCreditCustomerSubscription({
  refetchOnWindowFocus = false,
}: {
  refetchOnWindowFocus?: boolean | "always";
}): {
  status: any;
  data: any;
  error: any;
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  refetch: () => any;
} {
  window.logger("%c[useOrganizationAiCreditPurchase] useAiCreditCustomerSubscription", "color: #1976D2", {
    refetchOnWindowFocus,
  });
  return useQuery(["aiCreditCustomerSubscription"], getAiCreditCustomerSubscription, {
    refetchOnWindowFocus,
  });
}

const cancelAiCreditScheduledChange = async (params: { scheduleId: string }) => {
  return await apiPut({ path: "/ai_credit_purchases/cancel_scheduled_change", variables: params });
};

function useCancelAiCreditScheduledChange() {
  const queryClient = useQueryClient();
  return useMutation(cancelAiCreditScheduledChange, {
    onSuccess: () => {
      queryClient.invalidateQueries(["aiCreditCustomerSubscription"]);
      queryClient.invalidateQueries(["aiCreditSubscriptionSchedule"]);
    },
  });
}

const getAiCreditSubscriptionSchedule = async (scheduleId: string) => {
  return await apiGet({ path: `/ai_credit_purchases/subscription_schedule?schedule_id=${scheduleId}` });
};

function useAiCreditSubscriptionSchedule(scheduleId: string | null) {
  return useQuery(
    ["aiCreditSubscriptionSchedule", scheduleId],
    () => getAiCreditSubscriptionSchedule(scheduleId!),
    {
      enabled: !!scheduleId,
      refetchOnWindowFocus: false,
    },
  );
}

export {
  useOrganizationAiCreditPurchase,
  useCheckoutAiCreditTopUp,
  useAiCreditSubscriptionSchedule,
  useCancelAiCreditScheduledChange,
  usePreviewAiCreditSubscriptionChange,
  useCommitAiCreditSubscriptionChange,
  usePreviewAiCreditTopUp,
  usePurchaseAiCreditTopUp,
  usePurchaseAiCreditTopUpCheckoutSession,
  useCancelAiCreditSubscription,
  useRevertAiCreditSubscriptionCancellation,
  useOrganizationAiCreditPurchasePrices,
  useAiCreditCustomerSubscription,
};
